/* ---------------------------------------------------------------------------
   <cf-city-picker> — the searchable city control
   ---------------------------------------------------------------------------

   One searchable dropdown over the CUBE catalogue plus the visitor's own custom
   locations: grouped options, a panel portalled to <body> so it escapes every
   overflow clip on the way up the tree, and a scrim behind it.

   WHY IT LIVES HERE. It began inline in bundle-configurator-v2.liquid, where it
   could read the section's own variables directly. The trio configurator needs
   the same control, and `customElements.define` takes a name once per document:
   two copies would be two definitions, and — worse than throwing, since the
   define is guarded — two implementations free to drift apart. So it is one
   file, and the two sections differ only in what they hand it.

   THE HOST HANDS IT EVERYTHING. The element reads no globals and owns no state.
   Nothing about "five slots" or "three slots", nothing about prices, nothing
   about what a selection means. It renders options, tracks a selection through
   the host's accessors, and says when that changed.

       el.configure({
         slots:        () => 3,                  // how many may be chosen
         getSelection: () => [...],              // ids, '' for an empty slot
         setSelection: (next) => { ... },        // REPLACES the array
         variants:     () => [...],              // the catalogue
         designs:      () => [...],              // the visitor's custom locations
         label:        (variant) => 'Paris',
         optionLines:  (variant) => ['Paris', 'Eiffel Tower'], // optional, see below
         filters:      { bestseller: (variant) => true },     // optional, see below
         filter:       { get: () => '', set: (key) => {} },     // optional, see below
         startAt:      () => id,                                // optional, see below
         outOfStock:   (variant) => false,
         t:            { max_reached, status_selected, group_custom,
                         group_collection },
         onChange:     () => { ... },
       });

   A ROW IN TWO LINES, where the host asks for it with optionLines: the first,
   and the second under it — the CUBE Set gives a city's name, then its area. A
   chosen city shows in the field the same way, the name in bold there. The search still goes
   by `label`, kept on the row, so "berlin bra" finds "Berlin - Brandenburg
   Gate". Without optionLines a row and the field are the label, as ever.

   FILTERS UNDER THE SEARCH, where the panel has the buttons
   (snippets/cf-city-picker-filters.liquid, [data-bcv2-ms-filter="key"]) and the
   host says which city matches which key (`filters`, a test per key). One at a
   time: a press shows only those cities, together with whatever is searched,
   and a second press shows them all again. A button no city matches is hidden,
   so neither opens an empty list; without `filters` they all are.

   ONE FILTER FOR SEVERAL PICKERS, where the host hands `filter` — a get and a
   set over a value it keeps. The picker then reads which filter is on from the
   host each time it opens, and writes a press back there, so a filter turned on
   in one field is on in every field that shares it until it is turned off. The
   CUBE Set has a picker per city and one filter for all of them. Without
   `filter` each picker keeps its own, as before.

   WHERE THE LIST OPENS, where the host hands `startAt`: the id of the city to
   start from. Opened, the list scrolls so that city's row is at its top — the
   CUBE Set starts a field at its own city or, an empty one, at the city chosen
   last, so the next pick near it in the alphabet is not a scroll down from A.
   A city the search or the filter hides, or none, opens the list where it was.

   NOTHING FOUND: where the panel has snippets/cf-city-picker-empty.liquid, a
   search that matches no city shows it — "Location not found? Create your
   own." and a button to the Custom Location configurator, in a new tab.

   A PLACE OF ONE'S OWN, ALWAYS (2026-11-02, Alex): the same snippet holds the
   row "Create your own custom location", and where it is there the visitor's
   group heads the list even with no design in it yet — their designs, then
   that row, then the catalogue. A link, not an option: no box, never chosen,
   gone while a search or a filter is on. Either way to the configurator from
   here (the row, the button) calls awaitDesign(), and the design made there
   goes into this picker's place when the page hears of it
   (cf:designs-change, cf-city-data.js) and calls refresh() — into an empty
   place only, else it waits in the list.

   setSelection REPLACES rather than mutates, because removing a city rebuilds
   the array to keep the slots contiguous. A host holding its own reference to
   the old array would keep the stale one.

   ONE SLOT IS A DIFFERENT CONTROL, and slots() === 1 is how you ask for it. A
   cap of one would otherwise refuse the second pick — "you have reached the
   maximum of 1 city" — when with a single slot, picking another can only mean
   changing your mind. So one slot replaces instead of refusing, closes the
   panel on the pick the way a <select> closes, and puts the chosen city in the
   trigger where a count would be meaningless. The trio product hangs one of
   these on each of its three slots; the bundle configurator passes its real
   slot count and is untouched by any of it.

   CONFIGURE WHEN YOU LIKE. The inline version read the custom locations at parse
   time and said so in a comment, because connectedCallback built the rows
   synchronously and anything read later arrived too late. Rendering now waits
   for configure(), so that constraint is gone: call it whenever the data is
   ready, and call it again to re-render.
--------------------------------------------------------------------------- */

class CFCityPicker extends HTMLElement {
  configure(opts) {
    this.opts = opts || {};
    if (this.__wired) this.refresh();
    else if (this.isConnected) this.connectedCallback();
  }

  connectedCallback() {
    // Nothing to draw until the host has said what to draw. It will call
    // configure(), which comes back through here.
    if (this.__wired || !this.opts) return;
    this.__wired = true;

    this.trigger  = this.querySelector('.bcv2-ms-trigger');
    this.panel    = this.querySelector('[data-bcv2-ms-panel]');
    this.listEl   = this.querySelector('[data-bcv2-ms-list]');
    this.searchEl = this.querySelector('[data-bcv2-ms-search]');
    this.labelEl  = this.querySelector('[data-bcv2-ms-label]');
    this.countEl  = this.querySelector('[data-bcv2-ms-count]');
    this.statusEl = this.querySelector('[data-bcv2-ms-status]');
    this.closeBtn = this.querySelector('[data-bcv2-ms-close]');
    this.emptyEl  = this.querySelector('[data-bcv2-ms-empty]');

    this._originalParent = this.panel.parentNode;
    this._originalNextSibling = this.panel.nextSibling;

    // What the trigger says with nothing chosen. Read from the markup, so the
    // placeholder stays the host's to write and to translate.
    this._placeholder = this.labelEl ? this.labelEl.textContent : '';

    // A click opens the list with the search left alone, so the page does not
    // jump to it and a phone does not raise its keyboard before the shopper has
    // looked: the search is theirs to click. Opened from the keyboard (a click
    // with no pointer, detail 0), focus goes on into the search, or it would
    // be left behind on a field the list now covers.
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle({ focusSearch: e.detail === 0 });
    });
    this.closeBtn.addEventListener('click', () => this.close());
    this.searchEl.addEventListener('input', () => this.filter());

    this._filter = this.opts.filter?.get?.() || '';
    this.filterBtns = Array.from(this.querySelectorAll('[data-bcv2-ms-filter]'));
    this.filterBtns.forEach(btn => btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const key = btn.dataset.bcv2MsFilter;
      this._filter = this._filter === key ? '' : key;
      this.opts.filter?.set?.(this._filter);
      this.paintFilters();
      this.filter();
    }));

    // Close on click outside the panel (and outside the trigger).
    document.addEventListener('click', (e) => {
      if (this.panel.hidden) return;
      if (this.panel.contains(e.target)) return;
      if (this.trigger.contains(e.target)) return;
      this.close();
    });
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.panel.hidden) this.close();
    });
    window.addEventListener('resize', () => {
      if (!this.panel.hidden) this.positionPanel({ place: true });
    });
    // Off to the configurator, from the row or the nothing-found button: the list closes, and the design waits.
    this.panel.addEventListener('click', (e) => {
      if (!e.target.closest('[data-cf-design-new]')) return;
      this.awaitDesign();
      this.close();
    });

    this.renderOptions();
    this.updateUI();
  }

  /* ----- the host's data, never reached for directly ----- */
  get _slots()     { return Number(this.opts.slots?.() ?? 0); }
  get _selection() { return this.opts.getSelection?.() || []; }
  _select(next)    { this.opts.setSelection?.(next); }
  get _isSingle()  { return this._slots === 1; }
  _t(key)          { return (this.opts.t || {})[key] || ''; }

  refresh() {
    if (!this.__wired) return;
    this.renderOptions();
    this.placeAwaited();
    this.updateUI();
  }

  /* The visitor went to make a design from here: the designs there are now, so the next one is known as new. */
  awaitDesign() {
    this._awaiting = (this.opts.designs?.() || []).map(v => String(v.id));
  }

  /* The awaited design, newest first, into the first empty place - a place filled meanwhile is left as it is. */
  placeAwaited() {
    if (!this._awaiting) return;
    const fresh = (this.opts.designs?.() || []).find(v => !this._awaiting.includes(String(v.id)));
    if (!fresh) return;
    this._awaiting = null;
    const selection = this._selection.slice();
    const slots = this._slots;
    if (selection.filter(v => v !== '').length >= slots) return;
    const empty = selection.indexOf('');
    const at = empty !== -1 ? empty : selection.length;
    if (at >= slots) return;
    selection[at] = String(fresh.id);
    this._select(selection);
    this.opts.onChange?.({ source: 'design' });
  }

  isMobile() {
    return window.matchMedia('(max-width: 699px)').matches;
  }

  clearPosition() {
    ['position', 'top', 'bottom', 'left', 'width', 'maxHeight'].forEach(k => { this.panel.style[k] = ''; });
  }

  positionPanel({ place = false } = {}) {
    // Desktop: anchor the (body-portalled) panel to the trigger's position.
    if (this.isMobile()) {
      this.clearPosition();
      this._above = undefined;
      return;
    }
    const r = this.trigger.getBoundingClientRect();
    // As wide as the field, or wider where the host asks for it (--bcv2-ms-panel-min-width, in px): the CUBE Set's
    // fields sit under its cards and are narrower than a list of city names wants. Kept on the screen sideways.
    const min = parseFloat(getComputedStyle(this).getPropertyValue('--bcv2-ms-panel-min-width')) || 0;
    const width = Math.min(Math.max(r.width, min), window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    this.panel.style.position = 'fixed';
    this.panel.style.width = width + 'px';
    this.panel.style.left  = left + 'px';
    const below = window.innerHeight - r.bottom - 6 - 8;
    const above = r.top - 6 - 8;
    // Under the field, or above it when there is not room for the list below and there is more above — a field
    // near the bottom of the screen opened a list that ran off it. Decided when the list opens (and on a resize),
    // at its full height, and kept while it is open: a search that leaves a few cities makes it shorter, and the
    // list must not jump to the other side of its field for that.
    if (place || this._above === undefined) {
      this.panel.style.maxHeight = '';
      const height = this.panel.offsetHeight;
      this._cap = parseFloat(getComputedStyle(this.panel).maxHeight) || height;
      this._above = height > below && above > below;
    }
    // Above the field it hangs from its bottom edge, against the field, and a shorter list gives way at the top —
    // set by its top, a few cities left it hanging high over the field that opened it. As tall as the stylesheet
    // lets it be (theme.css, body > .bcv2-ms-panel), and never taller than the room on its side of the field.
    this.panel.style.maxHeight = Math.min(this._cap, Math.max(160, this._above ? above : below)) + 'px';
    if (this._above) {
      this.panel.style.top = 'auto';
      this.panel.style.bottom = (window.innerHeight - r.top + 6) + 'px';
    } else {
      this.panel.style.bottom = '';
      this.panel.style.top = (r.bottom + 6) + 'px';
    }
  }

  ensureScrim() {
    if (this._scrim) return this._scrim;
    const s = document.createElement('div');
    s.className = 'bcv2-ms-scrim';
    s.addEventListener('click', () => this.close());
    this._scrim = s;
    return s;
  }

  renderOptions() {
    this.listEl.innerHTML = '';
    const frag = document.createDocumentFragment();
    const designs  = this.opts.designs?.() || [];
    const variants = this.opts.variants?.() || [];

    const makeRow = (variant, group) => {
      const row = document.createElement('label');
      row.className = 'bcv2-ms-option';
      row.dataset.id = variant.id;
      row.dataset.group = group;
      const oos = this.opts.outOfStock ? this.opts.outOfStock(variant) : false;
      if (oos) row.classList.add('is-disabled');

      const label = this.opts.label(variant);
      row.dataset.label = label;
      const tests = this.opts.filters || {};
      row.dataset.filters = Object.keys(tests).filter(key => tests[key](variant)).join(' ');
      const lines = this.opts.optionLines ? this.opts.optionLines(variant) : null;
      const text = lines
        ? `<span class="bcv2-ms-option-name">${cfPickerEscape(lines[0])}</span>` +
          (lines[1] ? `<span class="bcv2-ms-option-area">${cfPickerEscape(lines[1])}</span>` : '')
        : cfPickerEscape(label);

      row.innerHTML = `
        <input type="checkbox" class="bcv2-ms-checkbox" value="${variant.id}" ${oos ? 'disabled' : ''}>
        <span class="bcv2-ms-option-label">${text}</span>
      `;

      const cb = row.querySelector('input');
      cb.addEventListener('change', () => this.onToggleOption(variant.id, cb));
      return row;
    };

    // A heading is its words alone. The custom one used to end in "View", a
    // link to the newest design's configurator: a way out of the list in the
    // middle of choosing from it.
    const makeHeading = (text, group) => {
      const h = document.createElement('div');
      h.className = 'bcv2-ms-group text-xs text-subdued bold';
      h.dataset.group = group;
      h.textContent = text;
      return h;
    };

    // The row to the configurator, from the panel's template (snippets/cf-city-picker-empty.liquid).
    const createTpl = this.panel.querySelector('template[data-bcv2-ms-create]');
    const create = createTpl ? createTpl.content.firstElementChild.cloneNode(true) : null;

    if (designs.length || create) {
      frag.appendChild(makeHeading(this._t('group_custom'), 'custom'));
      designs.forEach(v => frag.appendChild(makeRow(v, 'custom')));
      if (create) frag.appendChild(create);
      frag.appendChild(makeHeading(this._t('group_collection'), 'catalogue'));
      variants.forEach(v => frag.appendChild(makeRow(v, 'catalogue')));
    } else {
      variants.forEach(v => frag.appendChild(makeRow(v, 'catalogue')));
    }

    this.listEl.appendChild(frag);
    this.paintFilters();
    this.filter();
  }

  /* Which filter buttons show — those some city matches — and which is on. */
  paintFilters() {
    if (!this.filterBtns?.length) return;
    let any = false;
    this.filterBtns.forEach(btn => {
      const key = btn.dataset.bcv2MsFilter;
      const has = Boolean(this.listEl.querySelector(`.bcv2-ms-option[data-filters~="${CSS.escape(key)}"]`));
      btn.hidden = !has;
      any = any || has;
      if (!has && this._filter === key) this._filter = '';
    });
    this.filterBtns.forEach(btn => {
      const on = btn.dataset.bcv2MsFilter === this._filter;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
    });
    const row = this.querySelector('[data-bcv2-ms-filters]') || this.panel.querySelector('[data-bcv2-ms-filters]');
    if (row) row.hidden = !any;
  }

  onToggleOption(id, checkboxEl) {
    const idStr = String(id);
    const slots = this._slots;
    let selection = this._selection.slice();
    const currentSelected = selection.filter(v => v !== '');

    // ONE SLOT: THE PICK REPLACES WHATEVER IS THERE. Unticking the city the
    // slot already holds still empties it, which is how a shopper gets back to
    // the placeholder without choosing something they do not want.
    if (this._isSingle) {
      this._select([checkboxEl.checked ? idStr : '']);
      this.updateUI();
      this.opts.onChange?.({ source: 'multi-select' });
      if (checkboxEl.checked) this.close();
      return;
    }

    if (checkboxEl.checked) {
      // capacity check
      if (currentSelected.length >= slots) {
        checkboxEl.checked = false;
        const msg = this._t('max_reached');
        if (msg) alert(msg.replace('__M__', slots));
        return;
      }
      // append to first empty slot
      const emptyIdx = selection.indexOf('');
      const targetIdx = emptyIdx !== -1 ? emptyIdx : selection.length;
      if (targetIdx >= slots) {
        checkboxEl.checked = false;
        return;
      }
      selection[targetIdx] = idStr;
    } else {
      // remove: shift remaining selections down to keep contiguous ordering
      const idx = selection.indexOf(idStr);
      if (idx === -1) return;
      selection.splice(idx, 1);
      selection.push('');
      // ensure length stays at the slot count
      selection = selection.slice(0, slots);
      while (selection.length < slots) selection.push('');
    }

    this._select(selection);
    this.updateUI();
    this.opts.onChange?.({ source: 'multi-select' });
  }

  updateUI() {
    if (!this.__wired) return;
    const slots = this._slots;
    const selectedSet = new Set(this._selection.filter(v => v !== '').map(String));
    this.listEl.querySelectorAll('.bcv2-ms-option').forEach(row => {
      const id = row.dataset.id;
      const cb = row.querySelector('input');
      const isSel = selectedSet.has(id);
      cb.checked = isSel;
      row.classList.toggle('is-selected', isSel);
      // Mark disabled when at cap and not currently selected — but never with a
      // single slot, where being "at cap" is the normal state and locking the
      // rest would leave the shopper unable to change their mind.
      const atCap = !this._isSingle && selectedSet.size >= slots;
      if (atCap && !isSel && !row.classList.contains('is-disabled')) {
        cb.disabled = true;
        row.classList.add('is-cap-locked');
      } else if (!row.classList.contains('is-disabled')) {
        cb.disabled = false;
        row.classList.remove('is-cap-locked');
      }
    });

    const count = selectedSet.size;

    // A single slot says which city it holds. "1 / 1" and "1 of 1 selected"
    // are both true and both useless when the control stands for one choice,
    // and the name is the thing the shopper is actually looking for.
    if (this._isSingle) {
      const chosen = this._selection.find(v => v !== '');
      const row = chosen ? this.listEl.querySelector(`.bcv2-ms-option[data-id="${CSS.escape(String(chosen))}"]`) : null;
      const name = row ? (row.dataset.label || row.querySelector('.bcv2-ms-option-label').textContent) : '';
      // A row in two lines (optionLines) shows in the field as it does in the
      // list: the same markup, built from escaped text in renderOptions.
      const twoLines = row && row.querySelector('.bcv2-ms-option-name');
      if (this.labelEl) {
        if (twoLines) this.labelEl.innerHTML = row.querySelector('.bcv2-ms-option-label').innerHTML;
        else this.labelEl.textContent = name || this._placeholder;
      }
      this.classList.toggle('is-filled', Boolean(chosen));
      this.countEl.textContent = '';
      this.statusEl.textContent = '';
      return;
    }

    this.countEl.textContent = count > 0 ? `${count} / ${slots}` : '';
    this.statusEl.textContent = this._t('status_selected')
      .replace('__C__', count)
      .replace('__M__', slots);
  }

  filter() {
    const q = (this.searchEl.value || '').trim().toLowerCase();
    const shown = {};
    let any = false;
    this.listEl.querySelectorAll('.bcv2-ms-option').forEach(row => {
      const text = (row.dataset.label || row.textContent).toLowerCase();
      const kept = !this._filter || (row.dataset.filters || '').split(' ').includes(this._filter);
      const visible = kept && (!q || text.includes(q));
      row.style.display = visible ? '' : 'none';
      if (visible) shown[row.dataset.group] = true;
      any = any || visible;
    });
    // A search that finds nothing says where else the place can come from: the
    // Custom Location configurator (snippets/cf-city-picker-empty.liquid, where
    // the panel has it). Only for words typed — a filter alone never empties
    // the list, as a filter no city matches is hidden.
    if (this.emptyEl) this.emptyEl.hidden = !(q && !any);
    // The configurator's row stands only over the whole list: a search or a filter is looking for a city.
    this.listEl.querySelectorAll('.bcv2-ms-create').forEach(row => {
      const visible = !q && !this._filter;
      row.style.display = visible ? '' : 'none';
      if (visible) shown.custom = true;
    });
    // A heading over nothing is worse than no heading: search for a city and
    // "Your custom locations" would sit alone above the collection's hits.
    this.listEl.querySelectorAll('.bcv2-ms-group').forEach(h => {
      h.style.display = shown[h.dataset.group] ? '' : 'none';
    });
  }

  open({ focusSearch = false } = {}) {
    // ONE OPEN AT A TIME, across every picker in the document. The trigger's own
    // handler calls stopPropagation, so the click that opens this one never
    // reaches the outside-click listener that would have closed the others —
    // and with three of these on the trio page that left two panels portalled
    // to <body> at once, each with its own scrim. Harmless where there is only
    // one control on the page, which is why it never showed in the bundle
    // configurator.
    document.querySelectorAll('bcv2-multi-select').forEach(other => {
      if (other !== this && other.close && other.classList.contains('is-open')) other.close();
    });

    // Portal panel + scrim to <body> so they escape every containing
    // block / overflow clip on the way up the tab tree.
    const scrim = this.ensureScrim();
    document.body.appendChild(scrim);
    document.body.appendChild(this.panel);
    // THE PAGE LOCKS ON A PHONE ONLY (2026-10-02). There the panel is a sheet over
    // the bottom of the screen and the page behind it must not move. On a desktop
    // it is a dropdown under its field, and locking the page while it is open
    // froze the whole site for as long as one list was showing. It scrolls now,
    // and the panel — fixed to the screen at the field's position — follows the
    // field (positionPanel on scroll and resize, until close()). Any scroll, in
    // the capture phase: the CUBE Set's fields sit in a row that scrolls
    // sideways on its own, and the page's scroll alone left the list behind.
    if (this.isMobile()) {
      document.body.classList.add('bcv2-ms-locked');
    } else {
      this._follow = this._follow || (() => this.positionPanel());
      document.addEventListener('scroll', this._follow, { passive: true, capture: true });
    }

    // A filter the host shares may have been turned on or off in another picker since this one was last open.
    // Before the panel is placed, as it changes how many cities the list holds.
    if (this.opts.filter?.get) {
      const shared = this.opts.filter.get() || '';
      if (shared !== this._filter) {
        this._filter = shared;
        this.paintFilters();
        this.filter();
      }
    }

    this.panel.hidden = false;
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.positionPanel({ place: true });
    this.updateUI();
    this.scrollToStart();
    if (focusSearch) requestAnimationFrame(() => this.searchEl?.focus());
  }

  /* The host's city at the top of the list (startAt), when it is one the list is showing. */
  scrollToStart() {
    const id = this.opts.startAt?.();
    if (!id) return;
    const row = this.listEl.querySelector(`.bcv2-ms-option[data-id="${CSS.escape(String(id))}"]`);
    if (!row || row.style.display === 'none') return;
    this.listEl.scrollTop += row.getBoundingClientRect().top - this.listEl.getBoundingClientRect().top;
  }

  close() {
    this.panel.hidden = true;
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    // Return panel + scrim to their original DOM positions.
    if (this._scrim?.parentNode) this._scrim.parentNode.removeChild(this._scrim);
    if (this.panel.parentNode === document.body) {
      if (this._originalNextSibling && this._originalNextSibling.parentNode === this._originalParent) {
        this._originalParent.insertBefore(this.panel, this._originalNextSibling);
      } else {
        this._originalParent.appendChild(this.panel);
      }
    }
    // Reset inline styles applied for desktop positioning.
    this.clearPosition();
    this._above = undefined;
    document.body.classList.remove('bcv2-ms-locked');
    if (this._follow) document.removeEventListener('scroll', this._follow, { capture: true });
  }

  toggle(opts) { this.panel.hidden ? this.open(opts) : this.close(); }
}

function cfPickerEscape(str) {
  return String(str || '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

// The tag name is the one already in the markup and in theme.css, so neither
// moves. Guarded because both sections load this file and a visitor can have
// both in one document.
if (!customElements.get('bcv2-multi-select')) {
  customElements.define('bcv2-multi-select', CFCityPicker);
}
